import { useQuery } from "@tanstack/react-query";
import { ErrorType } from "../../shared/constants/errorTypes";
import { getCookieByName } from "@coworker/app/src/helpers/cookies";
import { makeServiceHeaders } from "@coworker/app/src/core/hooks/fetch.helpers";
import { CORE_SERVICE_URL } from "@coworker/app/src/hooks/API/core.service";

const upsertUserByEmail = async (email: string, uid: string) => {
  const impersonateStoreUser = getCookieByName(
    "impersonateStoreUser"
  ) as boolean;

  let headers: HeadersInit;

  try {
    if (typeof makeServiceHeaders !== "function") {
      console.error("makeServiceHeaders is undefined");
      return [];
    }

    headers = await makeServiceHeaders();
  } catch (error) {
    console.error("Error creating service headers:", error);
    return [];
  }

  const response = await fetch(`${CORE_SERVICE_URL}/auth/users/email`, {
    method: "PUT",
    headers: headers,
    body: JSON.stringify({
      email,
      uid,
      impersonateStoreUser: Boolean(impersonateStoreUser),
    }),
  });

  if (!response.ok) {
    let errorMessage = "";
    const contentType = response.headers.get("content-type") ?? "";

    if (contentType.includes("application/json")) {
      try {
        const errorBody = await response.json();
        errorMessage =
          errorBody?.message ?? errorBody?.error ?? JSON.stringify(errorBody);
      } catch {
        errorMessage = "";
      }
    } else {
      try {
        errorMessage = await response.text();
      } catch {
        errorMessage = "";
      }
    }

    const isMissingStoreAssignment =
      response.status === 403 &&
      errorMessage.toLowerCase().includes("not assigned to store");

    if (isMissingStoreAssignment) {
      throw new Error(ErrorType.NOTFOUND, { cause: response.status });
    }

    if (response.status === 404) {
      throw new Error(ErrorType.NOTFOUND, { cause: response.status });
    } else if (response.status === 403) {
      throw new Error(ErrorType.FORBIDDEN, { cause: response.status });
    } else if (response.status === 401) {
      throw new Error(ErrorType.UNAUTHORIZED, { cause: response.status });
    }
    throw new Error(ErrorType.INTERNALERROR);
  }

  const user = await response.json();
  return user?.data?.data;
};

export const useUpsertUserByEmail = (email: string, uid: string) => {
  return useQuery<any>({
    queryKey: ["user", email, uid],
    queryFn: async () => await upsertUserByEmail(email, uid),
    enabled: !!email && !!uid,
    throwOnError: false,
  });
};
